export interface TemplateTabReference {
  title: string;
  content: string;
}

export interface TemplateReferencePayload {
  title: string;
  tabs: TemplateTabReference[];
}

export function buildTemplateReferenceFromResolved(input: {
  docTitle: string;
  html: string;
  tabs?: { id?: string; title?: string; content?: string }[];
}): TemplateReferencePayload {
  if (input.tabs?.length) {
    return {
      title: input.docTitle,
      tabs: input.tabs.map((tab, index) => ({
        title: tab.title?.trim() || `Document ${index + 1}`,
        content: tab.content ?? '',
      })),
    };
  }
  return {
    title: input.docTitle,
    tabs: [{ title: input.docTitle, content: input.html }],
  };
}

function caseContextLines(
  caseData: Record<string, unknown> | null | undefined,
  docTitle: string,
  legalBasis?: string,
): string {
  return `Document type: ${docTitle}
Side represented: ${String(caseData?.representingSide || caseData?.sideRepresented || 'N/A')}
Primary relief sought: ${String(caseData?.primaryRelief || 'N/A')}
Legal basis: ${legalBasis || 'relevant Pakistani law'}`;
}

export function buildTemplateGuidedAiInstruction(input: {
  docTitle: string;
  caseData: Record<string, unknown> | null | undefined;
  legalBasis?: string;
  tabTitle?: string;
  tabIndex?: number;
  tabCount?: number;
}): string {
  const tabScope =
    input.tabCount != null &&
    input.tabCount > 1 &&
    input.tabIndex != null &&
    input.tabTitle
      ? `This is tab ${input.tabIndex + 1} of ${input.tabCount}: "${input.tabTitle}". Draft ONLY this document — do not include content from other tabs.\n\n`
      : '';

  return `${tabScope}You are drafting from a LexGAT reference template. Follow its structure, section headings, paragraph numbering, inline styles, and legal boilerplate EXACTLY.

Rules:
- Preserve all pre-filled header, party block, section titles, statutory citations, and signature block verbatim
- Customize ONLY factual/narrative sections using the case context
- Do NOT invent new sections or reorder existing ones
- Match HTML formatting (<p>, <strong>, <ol>/<ul>) used in the reference template
- Output the COMPLETE document as one continuous HTML string

${caseContextLines(input.caseData, input.docTitle, input.legalBasis)}`;
}

export function buildScaffoldAiInstruction(input: {
  docTitle: string;
  caseData: Record<string, unknown> | null | undefined;
  legalBasis?: string;
}): string {
  return `The document already has a populated court heading, party block, and signature block at the top and bottom. DO NOT modify, replace, or duplicate those — keep them exactly as they are.

Your task: fill in the body content between the header and signature block. Specifically:

- After the title/header, add "Respectfully Sheweth:" followed by numbered factual paragraphs describing the case
- Add grounds/legal arguments with proper Pakistani statutory citations
- Add a PRAYER section before the signature block
- Use the case context (parties, court, matter type) to make the content specific

${caseContextLines(input.caseData, input.docTitle, input.legalBasis)}

Output the COMPLETE document — header + your body content + signature block — as one continuous HTML document. Preserve the existing header and signature block verbatim.`;
}

export function buildBlankAiInstruction(input: {
  docTitle: string;
  caseData: Record<string, unknown> | null | undefined;
  legalBasis?: string;
}): string {
  return `Draft a complete ${input.docTitle} for the case. Side represented: ${String(input.caseData?.representingSide || input.caseData?.sideRepresented || 'N/A')}. Primary relief: ${String(input.caseData?.primaryRelief || 'N/A')}. Base it on ${input.legalBasis || 'relevant laws'}. Follow the LexGAT standard layout rules in the prompt (centered court/title, numbered parties, PRAYER section, lawyer signature block).`;
}

export function buildExemplarGuidedAiInstruction(input: {
  docTitle: string;
  exemplarTitle: string;
  caseData: Record<string, unknown> | null | undefined;
  legalBasis?: string;
  tabTitle?: string;
  tabIndex?: number;
  tabCount?: number;
}): string {
  const tabScope =
    input.tabCount != null &&
    input.tabCount > 1 &&
    input.tabIndex != null &&
    input.tabTitle
      ? `This is tab ${input.tabIndex + 1} of ${input.tabCount}: "${input.tabTitle}". Draft ONLY this document — do not include content from other tabs.\n\n`
      : '';

  return `${tabScope}This is a FORMATTING EXEMPLAR from the LexGAT library ("${input.exemplarTitle}") — not necessarily the exact document type requested ("${input.docTitle}").

Follow its layout conventions exactly: court heading alignment, title styling, party block, numbered paragraphs, PRAYER section, and lawyer signature block placement.
Adapt the legal content to the requested document type using the case context.
Do NOT invent a generic layout when the exemplar shows a specific structure.

${caseContextLines(input.caseData, input.docTitle, input.legalBasis)}`;
}
